import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
  Typography,
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  Grid,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

const FreeMedicineEdit = ({ open, orderData, onClose, token }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userToken = token || auth?.user?.token;

  const [patients, setPatients] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [patientInputValue, setPatientInputValue] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const {
    data: patientsData,
    isLoading: loadingPatients,
    refetch: refetchPatients,
  } = useFetchObjects(
    ["patients", page, searchTerm],
    `patients/?page=${page}&page_size=50&search=${searchTerm}`,
    userToken
  );

  const { data: stockData } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    userToken
  );

  const {
    handleUpdate,
    loading: updating,
    success,
  } = useUpdate("medicine-order-free", userToken);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success]);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    patient: null,
    items: [],
    total_price: 0,
    type: "",
    operation_room: "",
  });
  const [errors, setErrors] = useState({});

  // Load initial orderData
  useEffect(() => {
    if (!orderData) return;
    const pd = orderData.patient_details || null;
    const mappedItems = (orderData.items || []).map((it) => ({
      id: it.id,
      stock_medicine: it.stock_medicine,
      name: it.stock_medicine_display,
      quantity: it.quantity,
      sell_price: parseFloat(it.sell_price_display),
      total_price: parseFloat(it.sell_price_display) * it.quantity,
      available_stock: it.stock_medicine_quantity,
    }));
    const total = mappedItems.reduce((sum, it) => sum + it.total_price, 0);
    setInput({
      date: orderData.date,
      patient: pd,
      items: mappedItems,
      total_price: total,
      type: orderData.type || "",
      operation_room: orderData.operation_room || "",
    });
    if (pd) {
      setPatients((prev) => [pd, ...prev.filter((p) => p.id !== pd.id)]);
      setPatientInputValue(`${pd.firstname} ${pd.lastname || ""}`.trim());
    }
  }, [orderData]);

  // Append fetched patients
  useEffect(() => {
    if (!patientsData) return;
    setPatients((prev) => {
      const merged = [...prev, ...patientsData.results];
      return Array.from(new Map(merged.map((p) => [p.id, p])).values());
    });
    setHasMore(!!patientsData.next);
  }, [patientsData]);

  // Debounced search
  const debounced = useCallback(
    debounce((val) => setSearchTerm(val), 300),
    []
  );
  const onPatientInput = (_, val) => {
    setPatientInputValue(val);
    debounced(val);
    setPage(1);
  };

  const onPatientSelect = (_, val) =>
    setInput((prev) => ({ ...prev, patient: val }));

  const onScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 20 &&
      hasMore &&
      !loadingPatients
    ) {
      setPage((p) => p + 1);
    }
  };

  const changeItem = (i, field, val) => {
    setInput((prev) => {
      const items = [...prev.items];
      items[i][field] = val;
      items[i].total_price = parseFloat(
        (items[i].quantity * items[i].sell_price).toFixed(2)
      );
      const total = items.reduce((s, it) => s + it.total_price, 0);
      return { ...prev, items, total_price: total };
    });
  };

  const removeItem = (i) =>
    setInput((prev) => {
      const items = prev.items.filter((_, idx) => idx !== i);
      const total = items.reduce((s, it) => s + it.total_price, 0);
      return { ...prev, items, total_price: total };
    });

  const validate = () => {
    const errs = {};
    if (!input.patient?.id) errs.patient = t("pharmacy.patientRequired");
    if (!input.items.length) errs.items = t("pharmacy.atLeastOneMedicine");
    input.items.forEach((it, i) => {
      if (!it.quantity) errs[`q_${i}`] = t("pharmacy.quantityRequired");
    });
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSave = () => {
    if (!validate()) return;
    const payload = {
      date: input.date,
      patient: input.patient.id,
      type: input.type,
      operation_room: input.operation_room,
      items: input.items.map((it) => ({
        id: it.id,
        quantity: it.quantity,
        sell_price: it.sell_price,
      })),
    };
    handleUpdate(orderData?.id, payload);
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
          <Typography ml={1}>
            {t("pharmacy.editFreeMedicineOrder", "Edit Free Medicine Order")} #
            {orderData?.id}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ margin: "20px" }}>
        <TextField
          type="date"
          fullWidth
          label={t("pharmacy.orderDate")}
          InputLabelProps={{ shrink: true }}
          value={input.date}
          onChange={(e) =>
            setInput((prev) => ({ ...prev, date: e.target.value }))
          }
          sx={{ mb: 2, mt: 2 }}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="type"
              label="Section"
              variant="outlined"
              value={input.type}
              onChange={(e) => setInput({ ...input, type: e.target.value })}
              sx={{ backgroundColor: "#fff", borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="operation_room"
              label="Operation Room"
              variant="outlined"
              value={input.operation_room}
              onChange={(e) =>
                setInput({ ...input, operation_room: e.target.value })
              }
              sx={{ backgroundColor: "#fff", borderRadius: 2 }}
            />
          </Grid>
        </Grid>

        <Autocomplete
          options={patients}
          getOptionLabel={(o) =>
            o ? `${o.firstname} ${o.lastname || ""}` : ""
          }
          isOptionEqualToValue={(o, v) => o.id === v.id}
          inputValue={patientInputValue}
          onInputChange={onPatientInput}
          onChange={onPatientSelect}
          value={input.patient}
          loading={loadingPatients}
          ListboxProps={{ onScroll }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.patient")}
              error={!!errors.patient}
              helperText={errors.patient}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {" "}
                    {loadingPatients && <CircularProgress size={20} />}{" "}
                    {params.InputProps.endAdornment}{" "}
                  </>
                ),
              }}
            />
          )}
        />
        <Divider sx={{ my: 2 }} />
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("pharmacy.medicineName")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.availableStock")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                <TableCell align="center">{t("pharmacy.price")}</TableCell>
                <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
                <TableCell align="center">{t("pharmacy.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {input.items.map((it, i) => (
                <TableRow key={i} hover>
                  <TableCell>{it.name}</TableCell>
                  <TableCell align="center">{it.available_stock}</TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={it.quantity}
                      onChange={(e) =>
                        changeItem(
                          i,
                          "quantity",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      error={!!errors[`q_${i}`]}
                      helperText={errors[`q_${i}`] || ""}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={it.sell_price}
                      onChange={(e) =>
                        changeItem(
                          i,
                          "sell_price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {it.total_price.toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("pharmacy.removeItem")}>
                      <IconButton color="error" onClick={() => removeItem(i)}>
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">{t("pharmacy.totalPrice")}:</Typography>
          <Typography variant="h6">{input.total_price.toFixed(2)} ؋</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("pharmacy.cancel")}</Button>
        <Button variant="contained" onClick={handleSave} disabled={updating}>
          {updating
            ? t("pharmacy.saving", "Saving")
            : t("pharmacy.updateOrder", "Update Order")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FreeMedicineEdit;
